import React, { useEffect, useState } from "react";
import { CloudUpload, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import DriveConnectButton from "./components/DriveConnectButton";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Toast from "../shared/components/Toast";
import FAQSection from "./components/FAQSection";
import CaseStatusCard from "./components/CaseStatusCard";
import UploadOptions from "./components/UploadOptions";
import { useSession } from "../shared/hooks/useSession";
import { safeRedirect } from "../shared/utils/secureRedirect";
import { jwtDecode } from "jwt-decode";
import { getTokens, clearTokens } from "../shared/utils/secureTokenStorage";
import { fetchWithAuth, BASE_URL } from "../lender/api/client";
import { uploadCaseDocuments } from "../lender/api/cases";
import { validateFiles, sanitizeFile } from "../shared/utils/fileValidation";

export default function App() {
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [error, setError] = useState(null);
  const [caseNumber, setCaseNumber] = useState("");
  const [caseInfo, setCaseInfo] = useState(null);
  const [isCaseLoading, setIsCaseLoading] = useState(false);
  const [caseLookupError, setCaseLookupError] = useState("");
  const [toast, setToast] = useState(null);
  const [uploadComplete, setUploadComplete] = useState(false);

  // Calculate current step: 1 = Enter case, 2 = Upload docs, 3 = Complete
  const currentStep = uploadComplete ? 3 : caseInfo ? 2 : 1;

  // Session management with auto sign-out on inactivity
  useSession(user, () => {
    setToast({ message: "Session expired. Please sign in again.", type: "warning" });
    handleSignOut();
  });

  const handleCaseLookup = async (event) => {
    event.preventDefault();
    const trimmed = caseNumber.trim();
    if (!trimmed) {
      setCaseLookupError("Enter the case number to continue.");
      return;
    }
    setCaseLookupError("");
    setIsCaseLoading(true);
    try {
      const apiUrl = `${BASE_URL}/api/cases/by-number?caseNumber=${encodeURIComponent(trimmed)}`;
      console.log("🔍 Looking up case:", trimmed);

      const response = await fetchWithAuth(apiUrl, {
        headers: { accept: "application/json" }
      });

      console.log("📥 Response status:", response.status);

      if (!response.ok) {
        throw new Error("Unable to find that case. Please check the case number and try again.");
      }
      const data = await response.json();
      const payload = data?.data || data;
      setCaseInfo(payload);
      if (!user && (payload?.email || payload?.borrowerEmail || payload?.name)) {
        setUser({
          email: payload.email || payload.borrowerEmail || "",
          name: payload.name || ""
        });
      }
    } catch (lookupError) {
      console.error("❌ Case lookup error:", lookupError);
      setCaseLookupError("Unable to find that case. Please check the case number and try again.");
      setCaseInfo(null);
    } finally {
      setIsCaseLoading(false);
    }
  };

  const handleSignOut = () => {
    setUser(null);
    setMode(null);
    setSelectedFolder(null);
    setError(null);
    sessionStorage.clear();
    localStorage.clear();
    clearTokens();
    safeRedirect("/");
  };

  const handleLogoClick = () => {
    handleSignOut();
    safeRedirect("/");
  };

  const handleManualUpload = async (event) => {
    if (!caseInfo || !event.target.files?.length) return;
    const files = Array.from(event.target.files);
    event.target.value = "";

    const maxSize = 25 * 1024 * 1024;
    const tooLarge = files.find(f => f.size > maxSize);
    if (tooLarge) {
      setToast({ message: "File exceeds 25 MB limit.", type: "error" });
      return;
    }

    const validation = validateFiles(files);
    if (!validation.valid) {
      setToast({ message: "Upload failed: Invalid file type.", type: "error" });
      return;
    }

    const sanitizedFiles = validation.validFiles.map(sanitizeFile);
    setToast({ message: `Uploading ${sanitizedFiles.length} file(s)...`, type: "info" });

    const toPayload = (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = typeof reader.result === 'string' ? reader.result.split(',').pop() : '';
        resolve({
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          content: base64,
          source: "Borrower Upload"
        });
      };
      reader.onerror = () => reject(new Error("File read error"));
      reader.readAsDataURL(file);
    });

    try {
      const filePayloads = await Promise.all(sanitizedFiles.map(toPayload));
      await uploadCaseDocuments({
        caseId: caseInfo.id || caseInfo.caseId,
        files: filePayloads
      });
      setUploadComplete(true);
      setToast({ message: "Documents uploaded successfully!", type: "success" });
    } catch (error) {
      console.error("Upload error", error);
      setToast({ message: "Unable to upload documents. Please try again.", type: "error" });
    }
  };

  useEffect(() => {
    try {
      const tokens = getTokens();
      if (tokens) {
        const parsed = tokens;
        const token = parsed?.accessToken;
        if (token) {
          const decoded = jwtDecode(token);
          const emailFromToken = decoded?.email || decoded?.user || decoded?.sub;
          if (emailFromToken && !user) {
            setUser({ email: emailFromToken });
          }
        }
      }
    } catch {
      /* ignore token read errors */
    }
  }, [user]);

  // Enforce authentication - redirect to login if not authenticated
  useEffect(() => {
    const tokens = getTokens();
    const hasValidToken = tokens?.accessToken;

    if (!hasValidToken && !user && !caseInfo) {
      console.warn("Borrower portal requires authentication - clearing session and redirecting");
      handleSignOut();
    }
  }, [user, caseInfo]);

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <Header
        onLogoClick={handleLogoClick}
        userEmail={user?.email || user?.name || caseInfo?.email || caseInfo?.borrowerEmail}
        onSignOut={handleSignOut}
      />
      <div
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at 15% 20%, rgba(14,165,233,0.18), transparent 42%), radial-gradient(circle at 85% 15%, rgba(99,102,241,0.16), transparent 40%), linear-gradient(135deg, #0b1220 0%, #0f172a 55%, #0b1220 100%)",
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          padding: "100px 10px 140px",
        }}
      >
        <div style={{ maxWidth: "960px", margin: "0 auto 40px auto", textAlign: "center" }}>
          <h1
            style={{
              margin: "16px 0 12px 0",
              fontSize: "52px",
              lineHeight: 1.1,
              fontWeight: 900,
              color: "#e2e8f0",
            }}
          >
            Welcome to the new era
            <br />
            <span
              style={{
                background: "linear-gradient(90deg, #38bdf8, #60a5fa)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline-block",
                paddingBottom: "4px",
              }}
            >
              of home financing.
            </span>
          </h1>
        </div>

        {/* Progress Steps */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0",
          marginBottom: "32px",
          maxWidth: "500px",
          margin: "0 auto 32px auto"
        }}>
          {[
            { step: 1, label: "Enter Case" },
            { step: 2, label: "Upload Documents" },
            { step: 3, label: "Complete" }
          ].map(({ step, label }, index) => (
            <React.Fragment key={step}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                <div style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: step <= currentStep ? "linear-gradient(135deg, #38bdf8, #fbbf24)" : "rgba(148, 163, 184, 0.2)",
                  color: step <= currentStep ? "#fff" : "#94a3b8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "14px",
                  boxShadow: step === currentStep ? "0 0 16px rgba(56, 189, 248, 0.5)" : "none",
                  transition: "all 0.3s ease"
                }}>
                  {step < currentStep ? "✓" : step}
                </div>
                <span style={{
                  fontSize: "12px",
                  color: step <= currentStep ? "#e2e8f0" : "#64748b",
                  fontWeight: step === currentStep ? 600 : 400,
                  whiteSpace: "nowrap"
                }}>
                  {label}
                </span>
              </div>
              {index < 2 && (
                <div style={{
                  width: "80px",
                  height: "2px",
                  background: step < currentStep ? "linear-gradient(90deg, #38bdf8, #fbbf24)" : "rgba(148, 163, 184, 0.2)",
                  marginBottom: "24px",
                  transition: "all 0.3s ease"
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          style={{
            width: "640px",
            maxWidth: "95%",
            margin: "0 auto",
            padding: "44px",
            borderRadius: "20px",
            background: "linear-gradient(145deg, rgba(15,23,42,0.94), rgba(12,18,32,0.9))",
            boxShadow: "0 24px 80px rgba(8,12,24,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
            backdropFilter: "blur(18px)",
            border: "1px solid rgba(59,130,246,0.18)",
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Top right: Back + Sign Out */}
          {user && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "10px",
                marginBottom: "12px",
              }}
            >
              {mode && (
                <button
                  onClick={() => setMode(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#cbd5e1",
                    background: "none",
                    border: "1px solid rgba(59,130,246,0.4)",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "6px 10px",
                    borderRadius: "10px",
                    transition: "all 0.2s",
                    backdropFilter: "blur(6px)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(59,130,246,0.14)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <ArrowLeft size={14} style={{ marginRight: 6 }} /> Back
                </button>
              )}
            </div>
          )}

          {!caseInfo && (
            <>
              {/* Video hidden for now - uncomment to restore
              <div
                style={{
                  marginBottom: "28px",
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: "1px solid rgba(148,163,184,0.25)",
                  boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
                  background: "rgba(15,23,42,0.8)",
                }}
              >
                <div
                  style={{
                    padding: "14px 18px",
                    borderBottom: "1px solid rgba(148,163,184,0.2)",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#e2e8f0",
                    fontWeight: 700,
                    fontSize: "15px",
                  }}
                >
                  <CloudUpload size={18} />
                  Let's Get Started
                </div>
                <div
                  style={{
                    position: "relative",
                    paddingBottom: "56.25%",
                    height: 0,
                    overflow: "hidden",
                    background: "#000",
                  }}
                >
                  <iframe
                    title="Borrower onboarding video"
                    src="https://www.youtube.com/embed/yT6omjC-NKo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                  />
                </div>
              </div>
              */}

              <div style={{ marginBottom: 28 }}>
                <p
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "15px",
                    color: "#cbd5e1",
                    fontWeight: 600,
                  }}
                >
                  Enter your case number to continue
                </p>
                <form
                  onSubmit={handleCaseLookup}
                  style={{ display: "flex", gap: "10px", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}
                >
                  <input
                    type="text"
                    value={caseNumber}
                    onChange={(e) => setCaseNumber(e.target.value)}
                    placeholder="Check your email for your case number"
                    style={{
                      flex: 1,
                      minWidth: "240px",
                      padding: "12px 14px",
                      borderRadius: "12px",
                      border: "1px solid rgba(148,163,184,0.4)",
                      background: "rgba(255,255,255,0.04)",
                      color: "#e2e8f0",
                      outline: "none",
                    }}
                  />
                  <button
                    type="submit"
                    disabled={isCaseLoading}
                    style={{
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: "none",
                      background: "linear-gradient(135deg, #fbbf24, #fbbf24)",
                      color: "white",
                      fontWeight: 700,
                      minWidth: "110px",
                      cursor: "pointer",
                      boxShadow: "0 12px 40px rgba(14,165,233,0.3)",
                    }}
                  >
                    {isCaseLoading ? "Checking..." : "Continue"}
                  </button>
                </form>
                {caseLookupError && (
                  <p style={{ color: "#fca5a5", fontSize: "13px", marginTop: "10px" }}>{caseLookupError}</p>
                )}
              </div>

              <FAQSection />
            </>
          )}

          <CaseStatusCard caseInfo={caseInfo} />

          {caseInfo && (
            <>
              <div style={{ margin: "24px 0 36px" }}>
                <h2
                  style={{
                    margin: "0 0 8px 0",
                    fontWeight: 800,
                    fontSize: "32px",
                    background: "linear-gradient(135deg, #38bdf8 0%, #fbbf24 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {selectedFolder ? "Final Step: Share Documents" : "Here are the documents we need:"}
                </h2>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#cbd5e1",
                    fontWeight: 500,
                  }}
                >
                  {selectedFolder
                    ? "Confirm the selected folder is correct and click buttons below to finish."
                    : ""}
                </p>
              </div>

              {/* Tutorial Video / Checklist */}
              <AnimatePresence mode="wait">
                {!mode ? (
                  <motion.div
                    key="checklist"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <p
                      style={{
                        color: "#cbd5e1",
                        fontSize: "14px",
                        marginBottom: "20px",
                        lineHeight: 1.5,
                      }}
                    >
                    </p>
                    <ul
                      style={{
                        textAlign: "left",
                        color: "#cbd5e1",
                        fontSize: "13px",
                        marginBottom: "25px",
                        lineHeight: 1.6,
                      }}
                    >
                      <li> Government-issued ID (Driver's License or Passport)</li>
                      <li> Recent Pay Stubs (last 30–60 days)</li>
                      <li> Last 2 years of W-2s and Tax Returns</li>
                      <li> Recent Bank Statements (2–3 months)</li>
                      <li> Mortgage or Loan Statements (if applicable)</li>
                      <li> Proof of Assets (401k, brokerage, savings)</li>
                      <li> Property Insurance and Tax Bills (if owned)</li>
                    </ul>

                    <p
                      style={{
                        color: "#cbd5e1",
                        fontSize: "14px",
                        marginBottom: "20px",
                        lineHeight: 1.5,
                      }}
                    >
                      Once ready, click the button below to upload them securely.
                    </p>

                    <UploadOptions
                      onConnectDrive={() => setMode("drive")}
                      onManualUpload={handleManualUpload}
                      showDriveOption={false}  /* Set to true to re-enable Google Drive */
                    />

                  </motion.div>
                ) : (
                  <motion.div
                    key="upload"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    {mode === "drive" && (
                      <DriveConnectButton
                        autoStart
                        caseId={caseInfo?.caseId || caseInfo?.id || ""}
                        onFolderSelect={(folderId) => setSelectedFolder(folderId)}
                        onError={(message) => setToast({ message, type: "error" })}
                      />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}

          {error && (
            <p style={{ color: "red", fontSize: "13px", marginTop: "10px" }}>{error}</p>
          )}
        </motion.div>
      </div>
      <Footer />
    </>
  );
}
